import { NgStyle } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { ConfigService, CustomTopic, DifficultyLevel, GameEngineService, GameService, InternationalizationService, PlaySoundsService, SessionService, TransitionService, User } from '@app/core/services';
import { AddJeopardyUserAnswerInput, JeopardyRound, JeopardyUser } from '@app/shared/graphql/types';
import { AnswerViewModel, QuestionViewModel } from '@app/shared/rest-api';
import { BehaviorSubject, Subject, Subscription, timer } from 'rxjs';
import { debounceTime, filter, switchMap, take, tap } from 'rxjs/operators';
import { CountdownComponent } from '../../shared/countdown/countdown.component';

type IndexedAnswer = Omit<AnswerViewModel, 'explanation'> & { index: number, explanation: SafeHtml };
type ViewUser = JeopardyUser & { isCurrentPlayer: boolean, isActivePlayer: boolean, correctAnswer: boolean, wrongAnswer: boolean };

@Component({
  selector: 'app-question',
  templateUrl: './question.component.html',
  styleUrls: ['./question.component.scss'],
  imports: [NgStyle, CountdownComponent]
})
export class QuestionComponent implements OnInit {
  public questionTime: number;
  public question: QuestionViewModel;
  public choices: IndexedAnswer[];
  public correctAnswer: IndexedAnswer | null;
  public questionAnswered: boolean;
  private answerSelected: IndexedAnswer | null;
  public correctAnswerSelected: boolean;
  public isLastQuestion: boolean;

  public players: ViewUser[];

  private gameId: number;

  private currentUser: User;

  private difficultyLevels: DifficultyLevel[];
  public questionDifficulty: DifficultyLevel;

  public stopCountdownEvent: Subject<boolean> = new Subject<boolean>();
  public skipTokenDisabled: boolean;
  public fiftyTokenDisabled: boolean;

  private subscriptions: Subscription;
  public roundTimeStart$: Subject<Date>;
  public showExplanation: boolean;
  public selectedAnswerIndex: number;
  public trueAnswerIndex: number;

  public processingAnswer: boolean;
  public disabledAnswers: string[];
  private isMultiplayer: boolean;

  public connectedPlayer: JeopardyUser;
  public nextButtonActive: boolean;

  public nextRound: JeopardyRound;
  private currentRound: JeopardyRound;

  private loadDifficultLevels$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private init$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private initGame$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private initRound$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private initQuestion$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  public questionLoaded: boolean = false;
  public scoreDifference: string;

  private router = inject(Router);
  private soundsService = inject(PlaySoundsService);
  private gameService = inject(GameService);
  public i18n = inject(InternationalizationService);
  private sessionService = inject(SessionService);
  private transitionService = inject(TransitionService);
  private gameEngineService = inject(GameEngineService);
  private sanitizer = inject(DomSanitizer);
  private configService = inject(ConfigService);

  private topics: CustomTopic[];

  ngOnInit(): void {
    this.isLastQuestion = false;
    this.nextRound = null;
    this.nextButtonActive = false;
    this.isMultiplayer = false;
    this.processingAnswer = false;
    this.selectedAnswerIndex = null;
    this.trueAnswerIndex = null;
    this.roundTimeStart$ = new Subject();
    this.subscriptions = new Subscription();
    this.skipTokenDisabled = false;
    this.fiftyTokenDisabled = false;
    this.showExplanation = false;
    this.disabledAnswers = [];
    this.currentUser = this.sessionService.getUser();
    this.questionDifficulty = null;
    this.gameId = +this.gameService.getGameId();
    this.questionAnswered = false;
    this.answerSelected = null;
    this.correctAnswerSelected = false;
    this.question = null;
    this.choices = [];
    this.players = [];
    this.topics = this.gameEngineService.topics;

    this.changeBackground();

    this.init$.next(true);

    this.init$.pipe(
      filter(load => load),
      take(1)
    ).subscribe(() => {
      this.loadDifficultLevels$.next(true);

      if (!this.gameEngineService.gameId) {
        this.transitionService.showGameLoader(this.i18n.str('challengeu.msg_loading_question', 'Loading question...')());

        this.initGame$.next(true);
      } else {
        if (this.gameEngineService.active) {
          this.initRound$.next(true);
        } else {
          timer(500)
            .pipe(
              tap(() => {
                this.router.navigate(['final-scores']);

                setTimeout(() => {
                  this.transitionService.hideGameLoader();
                }, 500);
              }),
              take(1)
            )
            .subscribe();
        }
      }
    });

    this.loadDifficultLevels$.pipe(
      filter(load => load),
      switchMap(() => this.gameService.getDifficultyLevels()),
      tap(levels => {
        this.difficultyLevels = levels;
      }),
      take(1)
    ).subscribe();

    this.initGame$.pipe(
      filter(load => load),
      switchMap(() => this.gameEngineService.init(this.gameId)),
      take(1)
    ).subscribe(
      () => {
        this.initRound$.next(true);
      }
    );

    this.initRound$.pipe(
      filter(load => load),
      debounceTime(200),
      switchMap(() => this.gameEngineService.getCurrentRound()),
      take(1)
    ).subscribe(() => {
      //TODO: this.isMultiplayer = this.gameEngineService.isMultiplayer;
      this.currentRound = this.gameEngineService.currentRound;
      this.questionTime = this.currentRound.question?.timer || 30;

      if (this.currentRound.jeopardyUserAnswers.length > 0) {
        const userAnswers = this.currentRound.jeopardyUserAnswers.filter(answer => answer.userId === this.connectedPlayer?.userId);

        if (userAnswers.length) {
          this.questionAnswered = true;
          this.showExplanation = true;
        }
      }

      if (this.gameEngineService.roundStarted) {
        this.initQuestion$.next(true);
      } else {
        this.transitionService.hideGameLoader();
        this.router.navigate(['select-topic']);
      }
    });

    this.initQuestion$.pipe(
      filter(load => load),
      switchMap(() => this.gameService.getQuestionTranslation(this.currentRound.question.umbracoQuestionId, this.currentUser.location.language.isoCode4Letters)),
      take(1)
    ).subscribe(
      (question) => {
        // // q.MediaUrl = this.makeMediaURL(q.MediaUrl);
        const topic = this.topics?.find(topic => topic.name === question?.topic);

        this.question = {
          ...question,
          mediaDescription: question?.mediaUrl
            ? question.mediaUrl
            : topic?.defaultPictureUrl
              ? topic.defaultPictureUrlDescription
              : 'assets/app-branding/img/question/default-question-picture.jpg',
          mediaUrl: question?.mediaUrl
            ? this.configService.config().UmbracoApiBaseUrl + question.mediaUrl
            : topic?.defaultPictureUrl
              ? this.configService.config().UmbracoApiBaseUrl + topic.defaultPictureUrl
              : 'assets/app-branding/img/question/default-question-picture.jpg',
        };

        this.questionDifficulty = this.difficultyLevels.find(level => level.points === +this.question.points);

        this.choices = this.question.answers.map((answer, index) => ({
          ...answer,
          index,
          text: this.decodeBase64Utf8(answer.text),
          explanation: answer.explanation ? this.sanitizer.bypassSecurityTrustHtml(this.decodeBase64Utf8(answer.explanation)) : null,
        }));
        this.correctAnswer = this.choices.find(answer => answer.status) ?? null;

        this._initGame();
      }
    );
  }

  private _initGame(): void {
    this.connectedPlayer = this.gameEngineService.connectedPlayer;

    const fiftyToken = this.sessionService.getSession().fiftyToken;
    let usedInRound = false;

    const round = this.gameEngineService.currentRound;

    if (fiftyToken) {
      const usedFifty = new Map(JSON.parse(this.sessionService.getSession().fiftyToken));
      usedInRound = usedFifty.has(round.roundId);

      if (usedInRound) {
        const answers: any = usedFifty.get(round.roundId);

        if (answers && answers.length) {
          this.disabledAnswers = answers.map((answer: any) => answer.text);
        }
      }
    }

    const gameEngineDetails = this.gameEngineService.gameEngineDetails;

    console.log('gameEngineDetails', gameEngineDetails);
    this.skipTokenDisabled = this.connectedPlayer.jokerSkip >= gameEngineDetails.settingJokerSkip
      || this.sessionService.session.skipToken.includes(round.roundId)
      || this.connectedPlayer.userId !== this.currentRound.activeUser.userId;

    this.fiftyTokenDisabled = this.connectedPlayer.jokerFiftyFifty >= gameEngineDetails.settingJoker5050
      || usedInRound
      || this.connectedPlayer.userId !== this.currentRound.activeUser.userId;

    this._initSubscriptions(this.gameId);
    this._displayActivePlayers();

    this.questionLoaded = true;

    timer(100)
      .pipe(
        tap(() => {
          this.roundTimeStart$.next(this.currentRound.timeStart);
        }),
        take(1)
      )
      .subscribe();

    timer(500).pipe(
      tap(() => {
        this.transitionService.hideGameLoader();
      }),
      take(1)
    ).subscribe();
  }

  public topicIconUrl(topicName: string): string {
    const topic = this.topics?.find(topic => topic.name === topicName);
    return topic ? this.configService.config().UmbracoApiBaseUrl + topic.icon : undefined;
  }

  private _displayActivePlayers() {
    this.gameService.getUserByGameId(this.gameId)
      .pipe(
        tap((players) => {
          this.players = players.map(player => ({
            ...player,
            isCurrentPlayer: player.userId === this.connectedPlayer.userId,
            isActivePlayer: player.userId === this.currentRound.activeUser.userId,
            correctAnswer: false,
            wrongAnswer: false,
          }));
        }),
        take(1)
      )
      .subscribe();
  }

  private _initSubscriptions(gameId: number) {
    this.initRoundStartedSubscription(gameId);
    this.initRoundEndedSubscription(gameId);
    this.initGameEndedSubscription(gameId);

    this.initScoreUpdateSubscription(this.gameId);
  }

  private initScoreUpdateSubscription(gameId: number) {
    // user updated
    const playerScoreUpdated = this.gameService.getUserScoreUpdatedSubscription(gameId).subscribe(player => {
      const index = this.players.findIndex(p => p.userId === player.userId);

      if (index >= 0) {
        const diff = player.score - this.players[index]?.score;
        this.scoreDifference = diff > 0 ? '+ ' + diff : diff.toString();

        const viewPlayer: ViewUser = {
          ...player,
          isCurrentPlayer: player.userId === this.connectedPlayer.userId,
          isActivePlayer: player.userId === this.currentRound.activeUser?.userId,
          correctAnswer: diff > 0,
          wrongAnswer: diff < 0,
        };
        this.players.splice(index, 1, viewPlayer);
      }

      if (this.connectedPlayer.userId === player.userId) {
        this.sessionService.updateUserScore(player.score);
        this.soundsService.playSound('points-added');
      } else {
        // TODO: play sound for other players score update
      }
    });

    this.subscriptions.add(playerScoreUpdated);
  }

  private initRoundEndedSubscription(gameId: number) {
    // round ended
    const roundEndSub = this.gameService.getRoundEndedSubscription(gameId).subscribe(_ => {
      this.skipTokenDisabled = true;
      this.fiftyTokenDisabled = true;

      if (!this.answerSelected) {
        this.noAnswerProvided();
      }
      this.gameService.getGameRound(this.gameId).subscribe(round => {
        this.nextRound = round;
        this.nextButtonActive = true;
      });
    });

    this.subscriptions.add(roundEndSub);
  }

  private initGameEndedSubscription(gameId: number) {
    // game ended
    const gameEndSub = this.gameService.getGameInactivatedSubscription(gameId).subscribe(_ => {
      this.sessionService.updateUserHighestScore(this.sessionService.getSession().user.score);
      this.gameEngineService.clearGame();
      this.gameService.finishGame();
      this.sessionService.clearSession();

      setTimeout(() => {
        this.goToResultsPage();
      }, 5000);
    });
    this.subscriptions.add(gameEndSub);
  }

  private initRoundStartedSubscription(gameId: number) {
    // round started
    const roundStartSub = this.gameService.getRoundStartedSubscription(gameId).subscribe(_ => {
      // this.transitionService.showGameLoader('Loading question...');

      this.transitionService.showGameLoader(this.i18n.str('challengeu.msg_loading_question', 'Loading question...')());

      setTimeout(() => {
        this.router.navigate(['select-topic']);

        setTimeout(() => {
          this.transitionService.hideGameLoader();
        }, 500)
      }, 500);
    });

    this.subscriptions.add(roundStartSub);
  }

  private changeBackground() {
    // change background image
    document.documentElement.style.setProperty(`--main-bg-image`, "var(--question-bg-image)");
  }

  ngOnDestroy() {
    //this.soundsService.pauseMusic();
    this.initQuestion$.complete();
    this.subscriptions.unsubscribe();

    document.documentElement.style.setProperty(`--main-bg-image`, "var(--default-bg-image)");
  }

  nextQuestion(ev: MouseEvent) {
    ev.preventDefault();
    if (!this.questionAnswered) {
      return;
    }

    if (this.isLastQuestion) {
      this.goToResultsPage();
    } else {
      this.transitionService.showGameLoader(this.i18n.str('challengeu.msg_loading_next_round', 'Loading next round...')());

      timer(1000).subscribe(() => {
        this.router.navigate(['/select-topic']);

        if (this.isMultiplayer) {
          if (this.connectedPlayer.userId === this.nextRound.activeUser.userId) {
            setTimeout(() => {
              this.transitionService.hideGameLoader();
            }, 500);
          }
        } else {
          setTimeout(() => {
            this.transitionService.hideGameLoader();
          }, 500);
        }
      });
    }

    return;
  }

  // animated: true
  selectAnswer(ev: MouseEvent, index: number, animated: boolean = true) {
    ev.preventDefault();
    if (this.questionAnswered) {
      return;
    }

    this.stopCountdown();
    this.skipTokenDisabled = true;
    this.fiftyTokenDisabled = true;

    if (!animated) {
      this.doSubmitAnswer(ev, index);
      return;
    }

    this.selectedAnswerIndex = index;
    this.processingAnswer = true;
    this.soundsService.playSound('primary-button');

    setTimeout(() => {
      this.processingAnswer = false;
      this.doSubmitAnswer(ev, index);
    }, 750);
  }

  doSubmitAnswer(ev: MouseEvent, index: number) {
    ev.preventDefault();

    this.answerSelected = this.choices[index];

    const userAnswerInput: AddJeopardyUserAnswerInput = {
      roundId: this.currentRound.roundId,
      selectedAnswerIndex: index,
      trueAnswerIndex: this.correctAnswer.index,
      userId: this.connectedPlayer.userId,
      skip: false
    };

    const provideAnswerSub = this.gameService.provideAnswerToQuestion(this.gameId, userAnswerInput).subscribe(
      round => {
        this.questionAnswered = true;
        this.trueAnswerIndex = round.trueAnswerIndex;

        // this.isLastQuestion = this.round.roundNumber === this.noOfRounds;
        this.correctAnswerSelected = this.selectedAnswerIndex === this.trueAnswerIndex;

        if (this.correctAnswerSelected) {
          this.soundsService.playSound('correct-answer');
        } else {
          this.soundsService.playSound('wrong-answer');
        }

        setTimeout(() => {
          this.showExplanation = true;
        }, 500);
      },
      error => console.log(error)
    );

    this.subscriptions.add(provideAnswerSub);
  }

  private stopCountdown() {
    this.stopCountdownEvent.next(true);
  }

  public use5050token(event: Event) {
    event.preventDefault();
    this.fiftyTokenDisabled = true;

    this.soundsService.playSound('token-50');

    // let user: GameUser = this.getConnectedPlayerDetails(this.currentUser, this.gameDetails);;
    let user: JeopardyUser = this.connectedPlayer;
    user.jokerFiftyFifty = user.jokerFiftyFifty ? user.jokerFiftyFifty + 1 : 1;

    const updateUserSub = this.gameService.updateUser(user).subscribe(_ => {
      const answersIndexes = [...Array(this.choices.length).keys()].filter(e => e !== this.correctAnswer.index);
      const shuffled = answersIndexes
        .map(value => ({ value, sort: Math.random() }))
        .sort((a, b) => a.sort - b.sort)
        .map(({ value }) => +value);

      const disabled = [this.choices[shuffled[0]], this.choices[shuffled[1]]];
      this.disabledAnswers = disabled.map(answer => answer.text);

      const m = new Map();
      m.set(this.currentRound.roundId, disabled);

      this.sessionService.fiftyTokenUsed(JSON.stringify(Array.from(m.entries())));
    });
    this.subscriptions.add(updateUserSub);
  }

  public useSkipQuestionToken(event: Event) {
    event.preventDefault();

    this.skipTokenDisabled = true;
    this.fiftyTokenDisabled = true;
    this.stopCountdown();

    this.soundsService.playSound('token-skip');

    // this.isLastQuestion = this.round.roundNumber === this.noOfRounds;

    // if(this.isMultiplayer) {
    //   if(this.isLastQuestion) {
    //     this.goToResultsPage();
    //   } else {
    //     // TODO: display message waiting round end
    //   }
    // } else {
    //   if(this.isLastQuestion) {
    //     this.goToResultsPage();
    //   } else {
    //     // TODO skip to topic
    //   }
    // }

    const user = this.connectedPlayer;
    user.jokerSkip = user.jokerSkip ? user.jokerSkip + 1 : 1;

    const updateUserSub = this.gameService.updateUser(user).subscribe(console.log);
    this.subscriptions.add(updateUserSub);

    const userAnswerInput: AddJeopardyUserAnswerInput = {
      roundId: this.currentRound.roundId,
      selectedAnswerIndex: null,
      trueAnswerIndex: this.trueAnswerIndex ?? 0,
      userId: this.connectedPlayer.userId,
      skip: true
    };

    setTimeout(() => {
      const provideAnswerSub = this.gameService.provideAnswerToQuestion(this.gameId, userAnswerInput).subscribe(_ => {
        this.sessionService.skipTokenUsed(this.currentRound.roundId);

        // if(this.isLastQuestion) {
        //   this.sessionService.updateUserHighestScore(this.sessionService.getSession().user.score);
        //   this.router.navigate(['/final-scores']);
        // } else {
        //   // this.router.navigate(['/select-topic']);
        // }
      });

      this.subscriptions.add(provideAnswerSub);
    }, 500);
  }

  public noAnswerProvided() {
    this.answerSelected = null;
    this.stopCountdown();

    this.updateUserScore('no-answer', this.currentRound.roundId, null, false, this.trueAnswerIndex ?? 0);
  }

  private updateUserScore(status: string, roundId: number, selectedAnswerIndex: number | null, skip: boolean, trueAnswerIndex: number) {
    const userAnswerInput: AddJeopardyUserAnswerInput = {
      roundId,
      selectedAnswerIndex,
      trueAnswerIndex,
      userId: this.connectedPlayer.userId,
      skip
    };

    const provideAnswerSub = this.gameService.provideAnswerToQuestion(this.gameId, userAnswerInput).subscribe(_ => {
      this.questionAnswered = true;
      this.trueAnswerIndex = this.answerSelected?.index ?? 0;

      // this.isLastQuestion = this.round.roundNumber === this.noOfRounds;

      if (status === 'no-answer') {
        this.correctAnswerSelected = false;
      }

      this.soundsService.playSound('wrong-answer');

      setTimeout(() => {
        // TEST, to remove
        setTimeout(() => {
          this.showExplanation = true;
        }, 1000);
      }, 1000);
    });

    this.subscriptions.add(provideAnswerSub);
  }

  private goToResultsPage() {
    this.transitionService.showGameLoader(this.i18n.str('challengeu.msg_game_ended', 'Game ended. Loading results...')());
    this.sessionService.updateUserHighestScore(this.sessionService.getSession().user.score);
    this.gameEngineService.clearGame();

    this.subscriptions.unsubscribe();
    setTimeout(() => {
      this.router.navigate(['/final-scores']);

      setTimeout(() => {
        this.transitionService.hideGameLoader();
      }, 500);
    }, 2500);
  }

  decodeBase64Utf8(text: string): string {
    const bytes = Uint8Array.from(atob(text), c => c.charCodeAt(0));
    return new TextDecoder('utf-8').decode(bytes);
    }

}
